import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth, usePermissions } from './AuthContext';
import { atendimentoService, AtendimentoTicket } from '../services/atendimentoService';
import { atendimentoNotificationSoundService } from '../services/atendimentoNotificationSoundService';
import {
  AtendimentoVisibilidadeOptions,
  calcularBadgeAtendimento,
  deveIgnorarAtualizacaoTicketAtivo,
  deveTocarSomParaAtendimentoTicket,
  isAtendimentoTicketFechado,
  isAtendimentoTicketVisivel,
  mensagemNaoReabreTicketFechado,
} from '../utils/atendimentoTicketUtils';
import { env } from '@/utils/env';

interface AtendimentoNotificationContextType {
  socket: Socket | null;
  connected: boolean;
  badgeCount: number;
  totalNaoLidas: number;
  ticketsPendentes: number;
  temNovidade: boolean;
  setOpenTicketId: (ticketId: number | null) => void;
  marcarTicketComoLidoLocal: (ticketId: number) => void;
  on: (event: string, callback: (...args: any[]) => void) => () => void;
}

const AtendimentoNotificationContext = createContext<AtendimentoNotificationContextType | undefined>(undefined);

interface AtendimentoNotificationProviderProps {
  children: ReactNode;
}

export const AtendimentoNotificationProvider: React.FC<AtendimentoNotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { can } = usePermissions();

  const podeAcessarTickets = can('atendimento.tickets.acessar');
  const podeVerTicketsSemSetor = can('atendimento.ver_tickets_sem_setor');
  const podeVerTicketsOutrosUsuarios = can('atendimento.ver_tickets_outros_usuarios');
  const podeExibirTicketsBot = can('atendimento.exibir_tickets_bot');

  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef<Array<{ event: string; callback: (...args: any[]) => void }>>([]);
  const ticketsRef = useRef<AtendimentoTicket[]>([]);
  const openTicketIdRef = useRef<number | null>(null);
  const ticketsNovosIdsRef = useRef<Set<number>>(new Set());
  const userIdRef = useRef<number | null>(user?.id ? Number(user.id) : null);
  const visibilidadeRef = useRef<AtendimentoVisibilidadeOptions>({
    userId: user?.id ? Number(user.id) : null,
    podeVerTicketsSemSetor,
    podeVerTicketsOutrosUsuarios,
    podeExibirTicketsBot,
  });

  const [connected, setConnected] = useState(false);
  const [socketInstance, setSocketInstance] = useState<Socket | null>(null);
  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [openTicketId, setOpenTicketIdState] = useState<number | null>(null);

  useEffect(() => {
    userIdRef.current = user?.id ? Number(user.id) : null;
    visibilidadeRef.current = {
      userId: user?.id ? Number(user.id) : null,
      podeVerTicketsSemSetor,
      podeVerTicketsOutrosUsuarios,
      podeExibirTicketsBot,
    };
  }, [user?.id, podeVerTicketsSemSetor, podeVerTicketsOutrosUsuarios, podeExibirTicketsBot]);

  useEffect(() => {
    openTicketIdRef.current = openTicketId;
  }, [openTicketId]);

  const getVisibilidade = useCallback((): AtendimentoVisibilidadeOptions => visibilidadeRef.current, []);

  const atualizarTickets = useCallback((updater: (prev: AtendimentoTicket[]) => AtendimentoTicket[]) => {
    setTickets((prev) => {
      const next = updater(prev);
      ticketsRef.current = next;
      return next;
    });
  }, []);

  const aplicarTicket = useCallback((ticket: AtendimentoTicket) => {
    if (!isAtendimentoTicketVisivel(ticket, getVisibilidade())) return;

    atualizarTickets((prev) => {
      const exists = prev.some((item) => item.id === ticket.id);
      if (deveIgnorarAtualizacaoTicketAtivo(ticket, {
        ticketJaNaLista: exists,
        ticketAberto: openTicketIdRef.current === ticket.id,
      })) {
        // Mesma regra da AtendimentosPage: reaction/sticker nao reabrem ticket fechado.
        return prev;
      }
      return exists
        ? prev.map((item) => (item.id === ticket.id ? ticket : item))
        : [ticket, ...prev];
    });
  }, [atualizarTickets, getVisibilidade]);

  const removerTicket = useCallback((ticketId: number) => {
    atualizarTickets((prev) => prev.filter((item) => item.id !== ticketId));
  }, [atualizarTickets]);

  const handleNovoTicket = useCallback((ticket: AtendimentoTicket) => {
    if (deveTocarSomParaAtendimentoTicket(ticket, getVisibilidade(), openTicketIdRef.current)) {
      ticketsNovosIdsRef.current.add(ticket.id);
      atendimentoNotificationSoundService.playNewTicketSound();
    }

    aplicarTicket(ticket);
  }, [aplicarTicket, getVisibilidade]);

  const handleNovaMensagem = useCallback((data: { ticket?: AtendimentoTicket; mensagem?: { de_mim?: boolean; tipo?: string } }) => {
    if (!data?.ticket?.id) return;

    const ticket = data.ticket;
    const ticketAberto = openTicketIdRef.current === ticket.id;

    // reaction/sticker em ticket fechado nao atualizam badge/som da fila ativa.
    if (
      mensagemNaoReabreTicketFechado(data.mensagem?.tipo)
      && isAtendimentoTicketFechado(ticket)
      && !ticketAberto
    ) {
      return;
    }

    if (ticketAberto) {
      aplicarTicket({ ...ticket, msgs_nao_lidas: 0 });
      if (!mensagemNaoReabreTicketFechado(data.mensagem?.tipo)) {
        atendimentoService.marcarTicketComoLido(ticket.id).catch((err) => {
          console.error('Erro ao marcar atendimento como lido', err);
        });
      }
      return;
    }

    const usuarioId = userIdRef.current;
    const ticketUsuarioId = ticket.usuario_id ? Number(ticket.usuario_id) : null;
    const mensagemRecebida = data?.mensagem ? data.mensagem.de_mim === false : true;

    if (ticketsNovosIdsRef.current.has(ticket.id)) {
      ticketsNovosIdsRef.current.delete(ticket.id);
    } else if (
      !mensagemNaoReabreTicketFechado(data.mensagem?.tipo)
      && deveTocarSomParaAtendimentoTicket(ticket, getVisibilidade(), openTicketIdRef.current)
      && usuarioId
      && ticketUsuarioId === usuarioId
      && mensagemRecebida
    ) {
      atendimentoNotificationSoundService.playNewMessageSound();
    }

    aplicarTicket(ticket);
  }, [aplicarTicket, getVisibilidade]);

  const carregarTickets = useCallback(async () => {
    if (!podeAcessarTickets) {
      ticketsRef.current = [];
      setTickets([]);
      return;
    }

    try {
      const data = await atendimentoService.getTickets();
      ticketsRef.current = data;
      setTickets(data);
    } catch (err) {
      console.error('Erro ao carregar tickets de atendimento para notificacoes', err);
    }
  }, [podeAcessarTickets]);

  useEffect(() => {
    if (!user || !podeAcessarTickets) {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocketInstance(null);
      setConnected(false);
      ticketsRef.current = [];
      setTickets([]);
      return;
    }

    carregarTickets();

    const token = localStorage.getItem('sysup_token');
    if (!token) return;

    const socket = io(`${env.SOCKET_URL}/atendimento`, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    socketRef.current = socket;
    setSocketInstance(socket);

    listenersRef.current.forEach(({ event, callback }) => {
      socket.on(event, callback);
    });

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('atendimento:novo_ticket', handleNovoTicket);
    socket.on('atendimento:ticket_create', handleNovoTicket);
    socket.on('atendimento:ticket_atualizado', aplicarTicket);
    socket.on('atendimento:ticket_update', aplicarTicket);
    socket.on('atendimento:ticket_delete', (data: { ticketId?: number; id?: number }) => {
      const ticketId = data?.ticketId || data?.id;
      if (ticketId) removerTicket(ticketId);
    });
    socket.on('atendimento:nova_mensagem', handleNovaMensagem);

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setSocketInstance(null);
      setConnected(false);
    };
  }, [
    user,
    podeAcessarTickets,
    carregarTickets,
    handleNovoTicket,
    handleNovaMensagem,
    aplicarTicket,
    removerTicket,
  ]);

  const setOpenTicketId = useCallback((ticketId: number | null) => {
    setOpenTicketIdState(ticketId);
    openTicketIdRef.current = ticketId;
  }, []);

  const marcarTicketComoLidoLocal = useCallback((ticketId: number) => {
    atualizarTickets((prev) => prev.map((ticket) => (
      ticket.id === ticketId ? { ...ticket, msgs_nao_lidas: 0 } : ticket
    )));
  }, [atualizarTickets]);

  const on = useCallback((event: string, callback: (...args: any[]) => void) => {
    socketRef.current?.on(event, callback);
    listenersRef.current.push({ event, callback });

    return () => {
      socketRef.current?.off(event, callback);
      listenersRef.current = listenersRef.current.filter(
        (listener) => !(listener.event === event && listener.callback === callback)
      );
    };
  }, []);

  const badge = useMemo(
    () => calcularBadgeAtendimento(tickets, {
      userId: user?.id ? Number(user.id) : null,
      podeVerTicketsSemSetor,
      podeVerTicketsOutrosUsuarios,
      podeExibirTicketsBot,
    }, openTicketId),
    [tickets, user?.id, podeVerTicketsSemSetor, podeVerTicketsOutrosUsuarios, podeExibirTicketsBot, openTicketId]
  );

  const value = useMemo<AtendimentoNotificationContextType>(() => ({
    socket: socketInstance,
    connected,
    badgeCount: badge.badgeCount,
    totalNaoLidas: badge.totalNaoLidas,
    ticketsPendentes: badge.ticketsPendentes,
    temNovidade: badge.temNovidade,
    setOpenTicketId,
    marcarTicketComoLidoLocal,
    on,
  }), [socketInstance, connected, badge, setOpenTicketId, marcarTicketComoLidoLocal, on]);

  return (
    <AtendimentoNotificationContext.Provider value={value}>
      {children}
    </AtendimentoNotificationContext.Provider>
  );
};

export const useAtendimentoNotification = (): AtendimentoNotificationContextType => {
  const context = useContext(AtendimentoNotificationContext);
  if (context === undefined) {
    throw new Error('useAtendimentoNotification deve ser usado dentro de um AtendimentoNotificationProvider');
  }
  return context;
};
